import { useMemo } from 'react';
import { rowPaginationFeature, rowSortingFeature, tableFeatures, useTable } from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './table.jsx';
import { Button } from './button.jsx';
import { Skeleton } from './skeleton.jsx';
import { EmptyState } from './empty-state.jsx';
import { cn } from '../lib/utils.js';

const features = tableFeatures({ rowSortingFeature, rowPaginationFeature });
const EMPTY_ROWS = [];
const DEFAULT_PAGINATION = { pageIndex: 0, pageSize: 25 };
const DEFAULT_SORTING = [];

/**
 * Column meta understood by DataTable:
 *   meta: { numeric?: boolean, headerClassName?: string, cellClassName?: string }
 *
 * Server-side table: sorting and pagination are always "manual" – the parent fetches
 * the page (e.g. with TanStack Query, state in the query key) and passes `data` + `rowCount`.
 * Sorting maps to the API as `?sort=-field` (see `toSortParam`).
 *
 * @template T
 * @param {{
 *   columns: any[],
 *   data?: T[],
 *   rowCount?: number,
 *   pagination?: { pageIndex: number, pageSize: number },
 *   onPaginationChange?: (next: { pageIndex: number, pageSize: number }) => void,
 *   sorting?: Array<{ id: string, desc: boolean }>,
 *   onSortingChange?: (next: Array<{ id: string, desc: boolean }>) => void,
 *   pageSizeOptions?: number[],
 *   isLoading?: boolean,
 *   isFetching?: boolean,
 *   getRowId?: (row: T, index: number) => string,
 *   onRowClick?: (row: T) => void,
 *   emptyTitle?: string,
 *   emptyDescription?: string,
 *   emptyState?: import('react').ReactNode,
 *   label?: string,
 *   className?: string,
 * }} props
 */
export function DataTable({
  columns,
  data,
  rowCount,
  pagination = DEFAULT_PAGINATION,
  onPaginationChange,
  sorting = DEFAULT_SORTING,
  onSortingChange,
  pageSizeOptions = [25, 50, 100],
  isLoading = false,
  isFetching = false,
  getRowId,
  onRowClick,
  emptyTitle = 'Keine Einträge',
  emptyDescription,
  emptyState,
  label,
  className,
}) {
  const rows = data ?? EMPTY_ROWS;
  const total = rowCount ?? rows.length;

  const table = useTable({
    features,
    columns,
    data: rows,
    rowCount: total,
    getRowId,
    manualPagination: true,
    manualSorting: true,
    enableSorting: Boolean(onSortingChange),
    state: { sorting, pagination },
    onSortingChange: (updater) => onSortingChange?.(typeof updater === 'function' ? updater(sorting) : updater),
    onPaginationChange: (updater) => onPaginationChange?.(typeof updater === 'function' ? updater(pagination) : updater),
  });

  const pageCount = Math.max(1, Math.ceil(total / pagination.pageSize));
  const headerGroups = table.getHeaderGroups();
  const columnCount = headerGroups.at(-1)?.headers.length ?? columns.length;
  const skeletonRows = useMemo(() => Array.from({ length: Math.min(pagination.pageSize, 8) }, (_, i) => i), [pagination.pageSize]);
  const from = total === 0 ? 0 : pagination.pageIndex * pagination.pageSize + 1;
  const to = Math.min(total, (pagination.pageIndex + 1) * pagination.pageSize);

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className="relative rounded-lg border bg-card">
        {isFetching && !isLoading && <div className="absolute inset-x-0 top-0 z-20 h-0.5 animate-pulse bg-primary" aria-hidden="true" />}
        <Table aria-label={label} aria-busy={isLoading || isFetching || undefined} containerClassName="rounded-lg">
          <TableHeader>
            {headerGroups.map((group) => (
              <TableRow key={group.id} className="hover:bg-transparent">
                {group.headers.map((header) => {
                  const meta = header.column.columnDef.meta ?? {};
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  return (
                    <TableHead
                      key={header.id}
                      data-numeric={meta.numeric || undefined}
                      aria-sort={sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : canSort ? 'none' : undefined}
                      className={meta.headerClassName}
                    >
                      {header.isPlaceholder ? null : canSort ? (
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className={cn('inline-flex items-center gap-1 uppercase hover:text-foreground', meta.numeric && 'flex-row-reverse')}
                        >
                          <table.FlexRender header={header} />
                          {sorted === 'asc' ? (
                            <ArrowUp className="size-3.5" aria-hidden="true" />
                          ) : sorted === 'desc' ? (
                            <ArrowDown className="size-3.5" aria-hidden="true" />
                          ) : (
                            <ArrowUpDown className="size-3.5 opacity-50" aria-hidden="true" />
                          )}
                        </button>
                      ) : (
                        <table.FlexRender header={header} />
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              skeletonRows.map((i) => (
                <TableRow key={`skeleton-${i}`} className="hover:bg-transparent">
                  {Array.from({ length: columnCount }, (_, c) => (
                    <TableCell key={c}>
                      <Skeleton className="h-4 w-full max-w-40" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={columnCount} className="p-4">
                  {emptyState ?? <EmptyState compact title={emptyTitle} description={emptyDescription} className="border-0" />}
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} className={onRowClick ? 'cursor-pointer' : undefined} onClick={onRowClick ? () => onRowClick(row.original) : undefined}>
                  {row.getAllCells().map((cell) => {
                    const meta = cell.column.columnDef.meta ?? {};
                    return (
                      <TableCell key={cell.id} data-numeric={meta.numeric || undefined} className={meta.cellClassName}>
                        <table.FlexRender cell={cell} />
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {onPaginationChange && (
        <div className="flex flex-col gap-2 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <p className="numeric" aria-live="polite">
            {total === 0 ? 'Keine Einträge' : `${from.toLocaleString('de-DE')}–${to.toLocaleString('de-DE')} von ${total.toLocaleString('de-DE')}`}
          </p>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2">
              <span>Pro Seite</span>
              <select
                className="h-(--control-height-sm) rounded-md border border-input bg-card px-2 text-foreground"
                value={pagination.pageSize}
                onChange={(e) => onPaginationChange({ pageIndex: 0, pageSize: Number(e.target.value) })}
              >
                {pageSizeOptions.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
            <span className="numeric">
              Seite {pagination.pageIndex + 1} von {pageCount}
            </span>
            <Button
              variant="outline"
              size="icon-sm"
              onClick={() => onPaginationChange({ ...pagination, pageIndex: pagination.pageIndex - 1 })}
              disabled={pagination.pageIndex <= 0 || isLoading}
              aria-label="Vorherige Seite"
            >
              <ChevronLeft />
            </Button>
            <Button
              variant="outline"
              size="icon-sm"
              onClick={() => onPaginationChange({ ...pagination, pageIndex: pagination.pageIndex + 1 })}
              disabled={pagination.pageIndex + 1 >= pageCount || isLoading}
              aria-label="Nächste Seite"
            >
              <ChevronRight />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * TanStack sorting state → Laravel/spatie `sort` parameter ("-due_on,name").
 * @param {Array<{ id: string, desc: boolean }>} sorting
 */
export function toSortParam(sorting) {
  return sorting.map((s) => (s.desc ? `-${s.id}` : s.id)).join(',') || undefined;
}
